// PointerEvent ベースの統合入力ハンドラ。
// マウス / タッチ / タッチペンを同一に扱い、現在のツールに応じて動作を切り替える。
//
// - tool === "draw-pen": pointerdown でストローク開始、pointermove で増分、pointerup で確定
// - tool === "place-sticky" / "place-text" / "place-shape" / "place-section" / "place-table":
//   pointerdown で開始点を記録し、ドラッグ中はプレビューだけを表示して、pointerup で配置を確定する
//   （しきい値未満の移動はクリック配置、超えたらドラッグした矩形の大きさで配置）
// - tool === "place-stamp" / "place-comment": pointerdown でその場に配置する（入力ウィンドウを開く）
// - tool === "idle" の空白部 pointerdown: パン開始
// - tool === "idle" のオブジェクト pointerdown: transform 開始（実装は selection-overlay 側）
//
// ペン入力の終了契約:
// - 描画は開始したポインタが所有し、そのポインタの pointerup だけがストロークを確定する。
// - pointercancel と、pointerup を伴わない予期しない pointer capture 喪失では確定せず破棄する。
// - 所有ポインタ以外の move / up / cancel / lostpointercapture は無視する。
//
// ドラッグ配置（付箋 / テキスト / 図形 / セクション / 表）の終了契約も同様に、開始ポインタと
// capture 所有コンテナを固定する。予期しない capture 喪失、pointercancel（2 本指ピンチの開始を含む）、
// Esc などによるツールの切り替え、読み取り専用化、コンテナ差し替え、アンマウントでは配置せず破棄する。

import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { customAlphabet } from "nanoid";
import { useCanvasStore, type CanvasTool } from "../store/canvas-store";
import { PEN_THICKNESS_PIXELS } from "../../../lib/pen-preference";
import type { StrokePoint } from "../../../lib/pen-stroke";
import { PEN_TRANSIENT_MAX_POINTS } from "../../../lib/pen-stroke-payload";
import { CANVAS_SNAP_PX, screenToStage, type ViewState } from "../../../lib/canvas-coordinates";
import {
  ARROW_MIN_SIZE_PX,
  clampPointToCanvas,
  type StrokePattern,
} from "@refinement-canvas/domain";
import { resolvePenPreviewRect } from "../../../lib/pen-preview-rect";
import { useCanvasRect } from "../use-canvas-rect";
import { computeShapeDragFrame, type ShapeDragFrame } from "../../../lib/shape-arrow";
import {
  PLACEMENT_MIN_SIZE_PX,
  placementSizeLimitsOf,
  type PlacementSizeKey,
} from "../object-placement-defaults";
import { appendShiftLinePoint, resolveShiftLinePreviewPoints } from "../../../lib/pen-shift-line";
import { samplePenPreviewPoints } from "../../../lib/pen-stroke-payload";
import {
  applyCanvasStrokePattern,
  fillDottedStrokeStartFallback,
  resolveStrokePattern,
  strokeCenterlineLength,
} from "../../../lib/stroke-pattern";
import { isEditablePointerTarget } from "./editing-target";

const generateStrokeId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  16
);
// 描画中ストロークの他参加者向け中継の最小送信間隔（ms）。約 20Hz に間引いて通信量を抑える。
const PEN_TRANSIENT_THROTTLE_MS = 50;

// マウスのボタン番号（PointerEvent.button）。
const PRIMARY_BUTTON = 0; // 左ボタン
const MIDDLE_BUTTON = 1; // ホイール押し込み
const SECONDARY_BUTTON = 2; // 右ボタン
// 右ボタンドラッグによるパンを「ただの右クリック」と区別するしきい値（クライアント px）。
// これを超えて動いたときだけ、直後の contextmenu を抑止する（use-marquee-selection と揃える）。
const PAN_MOVE_THRESHOLD_PX = 4;

// ドラッグ配置（付箋・テキスト・図形・セクション・表）で「シングルクリック」と「ドラッグ」を判別する
// しきい値（クライアント px）。画面上の距離で判定するため拡大率に依存しない。これを超えて初めて
// ドラッグ配置（任意サイズ）として扱い、未満はクリック配置（記憶したサイズまたは既定サイズ）にする。
const SHAPE_DRAG_THRESHOLD_PX = 5;
// ドラッグ配置で確保する通常図形の最小サイズ（ステージ px）。退化（0 サイズ）を防ぐ。
// 通常図形はリサイズハンドルの最小サイズ（use-resize-handle.ts の MIN_SIZE_PX）と同じく、
// 配置サイズの下限の正本（object-placement-defaults.ts の PLACEMENT_MIN_SIZE_PX = 16）を使う。
// 矢印は線状の見た目へ枠を沿わせるため、短辺だけ ARROW_MIN_SIZE_PX を使う。
const SHAPE_DRAG_MIN_SIZE_PX = PLACEMENT_MIN_SIZE_PX;
// 参照専用（埋め込み）でパンを開始するドラッグしきい値（クライアント px）。
// この距離を超えて初めてドラッグとみなしてパンを開始する。しきい値未満のその場クリックは
// パンにせず、リンクカード等の要素側クリック（新規タブで開く等）を活かすため。
const READONLY_PAN_ACTIVATE_PX = 4;

/**
 * 参照専用でポインタを押してから、パンへ昇格する（または保留が解ける）までの間をキャンバスコンテナへ
 * 示す DOM 属性。styles.css がこの属性を起点に subtree へ grab カーソルを貫通させる。
 * ストアの state にしないのは、押下のたびに全オブジェクトを再描画させないためで、パン中の
 * data-canvas-panning（use-canvas-panning-attribute.ts）と同じ流儀にそろえている。
 */
export const READONLY_GRAB_ATTRIBUTE = "data-canvas-grab";

/**
 * 参照専用（埋め込み）でのパン保留状態。pointerdown 時点の位置と view を記録し、
 * 一定距離ドラッグされた時点でパンへ昇格させる（その場クリックはパンにしない）。
 * isSecondary は右ボタン起点かどうかで、パン成立後に直後の contextmenu を抑止する判定に使う。
 */
interface ReadOnlyPanPending {
  readonly x: number;
  readonly y: number;
  readonly panX: number;
  readonly panY: number;
  readonly pointerId: number;
  readonly isSecondary: boolean;
}

/**
 * ドラッグ配置（任意サイズ）に対応する配置ツール。付箋・テキスト・図形・セクション・表で
 * 同じジェスチャ（開始点の記録 → プレビュー → pointerup で確定）を共有する。
 */
type PlaceDragTool =
  | "place-sticky"
  | "place-text"
  | "place-shape"
  | "place-section"
  | "place-table";

function isPlaceDragTool(tool: CanvasTool): tool is PlaceDragTool {
  return (
    tool === "place-sticky" ||
    tool === "place-text" ||
    tool === "place-shape" ||
    tool === "place-section" ||
    tool === "place-table"
  );
}

/**
 * 図形以外のドラッグ配置ツールに対応する、配置サイズの制約（object-placement-defaults.ts）の単位。
 * 図形は種類ごとに既存のドラッグ規則（Shift の拘束・矢印の短辺の下限）を保つため対象外にする。
 */
const PLACE_DRAG_SIZE_KEYS: Readonly<
  Record<Exclude<PlaceDragTool, "place-shape">, PlacementSizeKey>
> = {
  "place-sticky": "sticky",
  "place-text": "text",
  "place-section": "section",
  "place-table": "table",
};

/**
 * ドラッグ配置の枠計算オプションをツール種別から得る。
 * セクションはタイトルバーが潰れないよう種別固有の最小サイズ（200×120）を確保する
 * （リサイズハンドル側の最小値と揃え、配置直後のリサイズで急に拡大される不整合を避ける）。
 * 表は最小未満のドラッグを既定サイズへ戻し、空の 3×3 表が潰れないようにする。
 * 付箋・テキストはリサイズハンドルと同じ 16px を下限とし（既定サイズは下限にしない）、
 * Shift による正方形の拘束は加えない。
 * 矢印は短辺だけ 8px を下限とし、線状の見た目より外接矩形が太くなるのを防ぐ。
 */
function placeDragFrameOptions(
  tool: PlaceDragTool,
  shiftHeld: boolean
): {
  gridSnap: boolean;
  snapPx: number;
  minSize: number;
  minW?: number | undefined;
  minH?: number | undefined;
  minShortSide?: number | undefined;
  constraint?: "none" | "axis" | "square" | undefined;
  arrow?: boolean | undefined;
} {
  const base = {
    gridSnap: useCanvasStore.getState().gridSnapEnabled,
    snapPx: CANVAS_SNAP_PX,
    minSize: SHAPE_DRAG_MIN_SIZE_PX,
  };
  if (tool !== "place-shape") {
    const limits = placementSizeLimitsOf(PLACE_DRAG_SIZE_KEYS[tool]);
    return { ...base, minW: limits.minW, minH: limits.minH };
  }
  if (useCanvasStore.getState().shapeKind === "arrow") {
    return {
      ...base,
      minShortSide: ARROW_MIN_SIZE_PX,
      arrow: true,
      constraint: shiftHeld ? "axis" : "none",
    };
  }
  return { ...base, constraint: shiftHeld ? "square" : "none" };
}

/**
 * ドラッグの始点・終点（ステージ座標）から、プレビューと確定に共通の配置枠を求める。
 * 付箋・テキストだけは、拡大率を下げた大きなドラッグでも配置サイズの上限（object-placement-defaults.ts）を
 * 超えないよう、開始点側の角を固定したまま幅・高さを上限へ丸める（既存の図形・セクション・表の規則は変えない）。
 */
function computePlaceDragFrame(
  tool: PlaceDragTool,
  start: { readonly x: number; readonly y: number },
  end: { readonly x: number; readonly y: number },
  shiftHeld: boolean
): ShapeDragFrame {
  const frame = computeShapeDragFrame(
    start.x,
    start.y,
    end.x,
    end.y,
    placeDragFrameOptions(tool, shiftHeld)
  );
  if (tool !== "place-sticky" && tool !== "place-text") return frame;
  const limits = placementSizeLimitsOf(PLACE_DRAG_SIZE_KEYS[tool]);
  const w = Math.min(frame.w, limits.maxW);
  const h = Math.min(frame.h, limits.maxH);
  if (w === frame.w && h === frame.h) return frame;
  // 左 / 上へ伸ばしたドラッグでは、開始点が枠の右端 / 下端にあるため、そちらを固定して縮める。
  return {
    ...frame,
    x: end.x < start.x ? frame.x + frame.w - w : frame.x,
    y: end.y < start.y ? frame.y + frame.h - h : frame.y,
    w,
    h,
  };
}

interface PenLayerHandle {
  readonly canvas: HTMLCanvasElement;
}

interface Handlers {
  /**
   * ストローク確定時に呼ばれる。サーバへ pen.stroke.commit op を送る用途。
   * strokeId は描画中の transient（pen.stroke.append）と同一の ID で、確定オブジェクトの
   * ID にこれを採用することで、確定時に該当ストロークのゴーストだけを正確に破棄できる。
   */
  readonly onStrokeCommit: (params: {
    strokeId: string;
    points: readonly StrokePoint[];
    color: string;
    thickness: number;
    strokePattern: StrokePattern;
  }) => void;
  /**
   * クリック配置の確定時に呼ばれる。付箋・テキスト・図形・セクション・表はしきい値未満の
   * pointerup で、スタンプ・コメントは pointerdown で呼ばれる。stagePoint は押下位置。
   */
  readonly onPlaceObject: (params: { stagePoint: { x: number; y: number } }) => void;
  /**
   * 付箋をドラッグで任意サイズ配置したときに呼ばれる。frame はグリッド吸着・最小サイズ（16px）・
   * 上限・サイズの非負化を反映済みのステージ座標枠。
   */
  readonly onPlaceStickyDrag: (params: { frame: ShapeDragFrame }) => void;
  /** テキストをドラッグで任意サイズ配置したときに呼ばれる。frame は付箋と同じ規則で求めた枠。 */
  readonly onPlaceTextDrag: (params: { frame: ShapeDragFrame }) => void;
  /**
   * 図形をドラッグで任意サイズ配置したときに呼ばれる。frame はグリッド吸着・最小サイズ・
   * サイズの非負化を反映済みのステージ座標枠で、矢印の起点角・起点辺も含む。
   */
  readonly onPlaceShapeDrag: (params: { frame: ShapeDragFrame }) => void;
  /**
   * セクションをドラッグで任意サイズ配置したときに呼ばれる。frame はグリッド吸着・
   * セクション固有の最小サイズ（200×120）・サイズの非負化を反映済みのステージ座標枠。
   */
  readonly onPlaceSectionDrag: (params: { frame: ShapeDragFrame }) => void;
  /** 表をドラッグで任意サイズ配置したときに呼ばれる。 */
  readonly onPlaceTableDrag: (params: { frame: ShapeDragFrame }) => void;
  /**
   * 描画中ストロークの増分点を他参加者へ中継する用途で呼ばれる（ゴースト表示用、任意）。
   * strokeId はストロークごとに一意で、同一ストロークの append 受信を束ねるために使う。
   */
  readonly onStrokeProgress?:
    | ((params: {
        strokeId: string;
        points: readonly StrokePoint[];
        strokePattern: StrokePattern;
      }) => void)
    | undefined;
}

interface Options {
  readonly stageRef: RefObject<HTMLElement | null>;
  readonly penLayerRef: RefObject<PenLayerHandle | null>;
  /** 同一ステージを使い回した場合も、連続スタンプのパン復帰先を持ち越さない。 */
  readonly canvasId?: string | undefined;
  /** 参照専用（埋め込み）。true で配置・描画を無効化し、空白ドラッグのパンだけを残す。 */
  readonly readOnly?: boolean | undefined;
}

export function usePointerMode(options: Options, handlers: Handlers): void {
  const readOnly = options.readOnly ?? false;
  // 入力中ペンプレビューの描画座標を、プレビュー用 Canvas の原点分だけずらすために使う。
  // プレビュー用 Canvas は CanvasPenLayer が「固定ステージ ∪ キャンバス矩形」へ配置・リサイズしているため、
  // 座標変換もその和集合矩形の原点で行う（矩形の外側から描き始めたストロークもプレビューへ収める）。
  const penPreviewRect = resolvePenPreviewRect(useCanvasRect());
  const stateRef = useRef<{
    drawing: boolean;
    // ペン描画を開始したポインタ。以降の move / up / cancel / lostpointercapture は
    // この所有ポインタのイベントだけを受け付け、他ポインタの割り込みで確定・破棄しない。
    drawingPointerId: number | null;
    panning: boolean;
    panStart: { x: number; y: number; panX: number; panY: number } | null;
    // パン終了時に元へ戻すツール（中ボタンパンなどで配置ツール中にパンした場合の復元用）。
    toolBeforePan: CanvasTool | null;
    // このパンが実際に動いたか（右ボタンパン直後の contextmenu 抑止の判定に使う）。
    panMoved: boolean;
    // このパンが右（副）ボタンで開始されたか。動いた場合に直後の contextmenu を抑止する。
    panIsSecondary: boolean;
    points: StrokePoint[];
    // 描画開始時に固定し、preview・transient・commit の全経路で同じ値を使う。
    strokePattern: StrokePattern;
    // Canvas preview に実際に描いた中心線長と最終点。点線の dash 位相を増分間でつなぐ。
    previewDistance: number;
    lastPreviewPoint: StrokePoint | null;
    // Shift 直線モードでは、確定済みのアンカー位置と暫定終端を点列から分離して保持する。
    shiftAnchorIndex: number | null;
    shiftPreviewPoint: StrokePoint | null;
    // 直線プレビューの全体再描画を 1 フレーム 1 回へまとめるための予約 ID。
    shiftRedrawRaf: number | null;
    // Shift 解除直後の再描画でも、表示用 sampler が確定 anchor を保持するための index。
    shiftRedrawAnchorIndex: number | null;
    // 描画中ストロークの他参加者中継用。strokeId と「未送信点の開始位置」「直近送信時刻」を保持する。
    strokeId: string | null;
    sentPointCount: number;
    lastTransientAt: number;
    // ドラッグ配置（付箋 / テキスト / 図形 / セクション / 表）の進行状態。開始点（ステージ / クライアント座標）と
    // しきい値超過判定、ジェスチャを開始したツール種別を保持する。
    shapePlacing: boolean;
    // 配置を開始したポインタ。move / up / cancel / lostpointercapture は所有者だけを受け付ける。
    shapePointerId: number | null;
    shapeStart: { stageX: number; stageY: number; clientX: number; clientY: number } | null;
    shapeDragActive: boolean;
    // 最後にプレビューへ反映した枠。pointerup では再計算せず、この値を確定に使う。
    shapeLastFrame: ShapeDragFrame | null;
    // 参照専用（埋め込み）でのパン保留状態。詳細は ReadOnlyPanPending の説明を参照。
    // 代入は必ず effect 内の setReadOnlyPanPending を通し、data-canvas-grab 属性と同期させる。
    readOnlyPanPending: ReadOnlyPanPending | null;
    placeDragTool: PlaceDragTool | null;
  }>({
    drawing: false,
    drawingPointerId: null,
    panning: false,
    panStart: null,
    toolBeforePan: null,
    panMoved: false,
    panIsSecondary: false,
    points: [],
    strokePattern: "solid",
    previewDistance: 0,
    lastPreviewPoint: null,
    shiftAnchorIndex: null,
    shiftPreviewPoint: null,
    shiftRedrawRaf: null,
    shiftRedrawAnchorIndex: null,
    strokeId: null,
    sentPointCount: 0,
    lastTransientAt: 0,
    shapePlacing: false,
    shapePointerId: null,
    shapeStart: null,
    shapeDragActive: false,
    shapeLastFrame: null,
    readOnlyPanPending: null,
    placeDragTool: null,
  });
  // 右ボタンドラッグによるパン直後の contextmenu を 1 回だけ抑止するためのフラグ
  // （use-marquee-selection の同名の仕組みと同趣旨。反転 ON 時の右ドラッグパン用）。
  const suppressContextMenuRef = useRef(false);
  // 配置開始時に capture を取得したコンテナ。ref の差し替え後も旧要素から確実に解放するため、
  // 現在の stageRef とは分離して保持する。
  const shapeCaptureOwnerRef = useRef<HTMLElement | null>(null);
  const stampPanCaptureRef = useRef<{ container: HTMLElement; pointerId: number } | null>(null);

  useLayoutEffect(() => {
    const cancelStampPan = (): void => {
      if (stateRef.current.toolBeforePan !== "place-stamp") return;
      // パン中の画面切替でも、pointerup が旧スタンプツールを復活させないよう先に破棄する。
      stateRef.current.toolBeforePan = null;
      stateRef.current.panning = false;
      stateRef.current.panStart = null;
      stateRef.current.panMoved = false;
      stateRef.current.panIsSecondary = false;
      const state = useCanvasStore.getState();
      if (state.tool === "panning") state.setTool("idle");
      const capture = stampPanCaptureRef.current;
      stampPanCaptureRef.current = null;
      if (capture?.container.hasPointerCapture(capture.pointerId)) {
        capture.container.releasePointerCapture(capture.pointerId);
      }
    };
    if (readOnly) {
      cancelStampPan();
      const state = useCanvasStore.getState();
      if (state.tool === "place-stamp") state.setTool("idle");
    }
    return cancelStampPan;
  }, [options.canvasId, readOnly]);

  /**
   * 配置状態とプレビューを先に初期化し、確定・破棄処理が参照するスナップショットを返す。
   * store 更新で再レンダーが起きても、中途半端な配置状態が新しいハンドラへ見えない順序にする。
   */
  const takeShapePlacement = useRef(() => {
    const snapshot = {
      shapeStart: stateRef.current.shapeStart,
      dragActive: stateRef.current.shapeDragActive,
      lastFrame: stateRef.current.shapeLastFrame,
      placeDragTool: stateRef.current.placeDragTool,
      pointerId: stateRef.current.shapePointerId,
      captureOwner: shapeCaptureOwnerRef.current,
    };
    stateRef.current.shapePlacing = false;
    stateRef.current.shapePointerId = null;
    stateRef.current.shapeStart = null;
    stateRef.current.shapeDragActive = false;
    stateRef.current.shapeLastFrame = null;
    stateRef.current.placeDragTool = null;
    useCanvasStore.getState().setShapePlacementDraft(null);
    return snapshot;
  }).current;

  /** capture 解放は状態初期化後に行い、解放中に lostpointercapture が届いても二重処理させない。 */
  const releaseShapePointerCapture = useRef(
    (captureOwner: HTMLElement | null, pointerId: number | null): void => {
      try {
        if (
          captureOwner !== null &&
          pointerId !== null &&
          captureOwner.hasPointerCapture(pointerId)
        ) {
          captureOwner.releasePointerCapture(pointerId);
        }
      } finally {
        shapeCaptureOwnerRef.current = null;
      }
    }
  ).current;

  /** 配置を確定せず破棄する共通経路。開始ツールから切り替わっていれば現在の選択を尊重する。 */
  const discardShapePlacement = useRef((releaseCapture: boolean): void => {
    if (!stateRef.current.shapePlacing) return;
    const placement = takeShapePlacement();
    if (
      placement.placeDragTool !== null &&
      useCanvasStore.getState().tool === placement.placeDragTool
    ) {
      useCanvasStore.getState().setTool("idle");
    }
    if (releaseCapture) {
      releaseShapePointerCapture(placement.captureOwner, placement.pointerId);
    } else {
      shapeCaptureOwnerRef.current = null;
    }
  }).current;

  // ハンドラオブジェクトだけの再生成では配置を破棄しない。一方、読み取り専用化または
  // stageRef の実体差し替えは入力ライフタイムの境界なので、旧所有要素の capture を解放して中断する。
  useLayoutEffect(() => {
    const currentContainer = options.stageRef.current;
    if (
      stateRef.current.shapePlacing &&
      (readOnly || shapeCaptureOwnerRef.current !== currentContainer)
    ) {
      discardShapePlacement(true);
    }
  });

  // アンマウント時も、進行中の配置と旧コンテナに残る capture を確実に片付ける。
  useEffect(
    () => () => {
      discardShapePlacement(true);
    },
    [discardShapePlacement]
  );

  useEffect(() => {
    const container = options.stageRef.current;
    if (!container) return undefined;

    /**
     * 参照専用のパン保留と、それを表す data-canvas-grab 属性を必ず同時に更新する。
     * 属性だけが残ると grab カーソルが出たままになり、付け忘れると押下中に grab が出ない。
     * 保留への代入は、設定・破棄・昇格・クリーンアップのすべてでこの関数だけを通す。
     */
    const setReadOnlyPanPending = (pending: ReadOnlyPanPending | null): void => {
      stateRef.current.readOnlyPanPending = pending;
      if (pending === null) {
        container.removeAttribute(READONLY_GRAB_ATTRIBUTE);
      } else {
        container.setAttribute(READONLY_GRAB_ATTRIBUTE, "true");
      }
    };

    // この effect は依存配列を持たず毎レンダーで貼り直され、クリーンアップで属性を外す。
    // 保留が続いている間は貼り直しのたびに復元し、押下中の再描画で grab が消えないようにする。
    // 参照専用でなくなった場合は保留ごと破棄し、編集ビューへ grab を持ち越さない。
    setReadOnlyPanPending(readOnly ? stateRef.current.readOnlyPanPending : null);

    // パン開始の共通処理。開始時のツールを覚えておき、終了時に復元する（中ボタンパンで配置ツール中に
    // パンしても、離したらそのツールへ戻せるようにする）。isSecondary は右ボタン起点かどうかで、
    // 実際に動いた場合に直後の contextmenu を抑止する判定に使う。
    const beginPan = (
      event: PointerEvent,
      view: ViewState,
      toolBeforePan: CanvasTool,
      isSecondary: boolean
    ): void => {
      stateRef.current.panning = true;
      stateRef.current.panStart = {
        x: event.clientX,
        y: event.clientY,
        panX: view.panX,
        panY: view.panY,
      };
      stateRef.current.toolBeforePan = toolBeforePan;
      stampPanCaptureRef.current =
        toolBeforePan === "place-stamp" ? { container, pointerId: event.pointerId } : null;
      stateRef.current.panMoved = false;
      stateRef.current.panIsSecondary = isSecondary;
      useCanvasStore.getState().setTool("panning");
      container.setPointerCapture(event.pointerId);
    };

    // 入力中ペンプレビュー（専用 Canvas）を消す。確定後と破棄後の両方で使う。
    const clearPenPreview = (): void => {
      const handle = options.penLayerRef.current;
      if (!handle) return;
      const ctx = handle.canvas.getContext("2d");
      ctx?.clearRect(0, 0, handle.canvas.width, handle.canvas.height);
    };

    /** 予約済みの直線プレビュー再描画を取り消し、予約状態を初期化する。 */
    const cancelShiftRedraw = (): void => {
      const raf = stateRef.current.shiftRedrawRaf;
      if (raf !== null) window.cancelAnimationFrame(raf);
      stateRef.current.shiftRedrawRaf = null;
    };

    /** Shift 再描画用の有限点列を Canvas へ描き、次の増分描画へ位相を引き継ぐ。 */
    const redrawPenPreview = (points: readonly StrokePoint[], anchorIndex: number | null): void => {
      const handle = options.penLayerRef.current;
      if (!handle) return;
      const ctx = handle.canvas.getContext("2d");
      if (!ctx) return;
      const displayPoints = samplePenPreviewPoints(
        points,
        anchorIndex === null ? [0, points.length - 1] : [0, anchorIndex, points.length - 1]
      );
      const color = useCanvasStore.getState().penColor;
      const thickness = PEN_THICKNESS_PIXELS[useCanvasStore.getState().penThickness];
      const pattern = stateRef.current.strokePattern;
      ctx.clearRect(0, 0, handle.canvas.width, handle.canvas.height);
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = color;
      ctx.lineWidth = thickness;
      applyCanvasStrokePattern(ctx, thickness, pattern);
      const first = displayPoints[0];
      if (first && displayPoints.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(first.x - penPreviewRect.x, first.y - penPreviewRect.y);
        for (const point of displayPoints.slice(1)) {
          ctx.lineTo(point.x - penPreviewRect.x, point.y - penPreviewRect.y);
        }
        ctx.stroke();
      }
      fillDottedStrokeStartFallback(ctx, displayPoints, thickness, pattern, color, (point) => ({
        x: point.x - penPreviewRect.x,
        y: point.y - penPreviewRect.y,
      }));
      ctx.restore();
      stateRef.current.previewDistance = strokeCenterlineLength(displayPoints);
      stateRef.current.lastPreviewPoint = displayPoints[displayPoints.length - 1] ?? null;
    };

    /**
     * 直線モードに関わるフレームだけ、確定点列と暫定終端をまとめて描き直す。
     * 通常の手書き中は従来の増分描画を維持する。
     */
    const scheduleShiftPreviewRedraw = (): void => {
      if (stateRef.current.shiftAnchorIndex !== null) {
        stateRef.current.shiftRedrawAnchorIndex = stateRef.current.shiftAnchorIndex;
      }
      if (stateRef.current.shiftRedrawRaf !== null) return;
      stateRef.current.shiftRedrawRaf = window.requestAnimationFrame(() => {
        stateRef.current.shiftRedrawRaf = null;
        const activeAnchorIndex = stateRef.current.shiftAnchorIndex;
        const fixedAnchorIndex = activeAnchorIndex ?? stateRef.current.shiftRedrawAnchorIndex;
        const points =
          activeAnchorIndex === null
            ? stateRef.current.points
            : resolveShiftLinePreviewPoints(
                stateRef.current.points,
                activeAnchorIndex,
                stateRef.current.shiftPreviewPoint
              );
        stateRef.current.shiftRedrawAnchorIndex = null;
        redrawPenPreview(points, fixedAnchorIndex);
      });
    };

    /** 未送信の確定点だけを他参加者へ中継する。 */
    const flushStrokeProgress = (throttled: boolean): void => {
      const onStrokeProgress = handlers.onStrokeProgress;
      const strokeId = stateRef.current.strokeId;
      if (!onStrokeProgress || !strokeId) return;

      const now = Date.now();
      if (throttled && now - stateRef.current.lastTransientAt < PEN_TRANSIENT_THROTTLE_MS) {
        return;
      }
      const allPoints = stateRef.current.points;
      const pendingPoints = allPoints.slice(stateRef.current.sentPointCount);
      if (pendingPoints.length === 0) return;
      // 1 通あたりの点数はサーバスキーマの上限（pen.stroke.append は最大 512 件）で区切る。
      // 超えた分は捨てずに次回の flush で送る（次の pointermove かスロットル満了で必ず来るため、
      // プレビューの遅れは 1 スロットル周期に収まる）。
      const newPoints = pendingPoints.slice(0, PEN_TRANSIENT_MAX_POINTS);
      stateRef.current.lastTransientAt = now;
      stateRef.current.sentPointCount += newPoints.length;
      onStrokeProgress({
        strokeId,
        points: newPoints,
        strokePattern: stateRef.current.strokePattern,
      });
    };

    /**
     * Shift の現在状態へ直線モードを同期する。キーイベントで境界を確定し、pointermove は
     * ウィンドウ非アクティブ中にキー状態を取りこぼした場合の同期経路も兼ねる。
     */
    const syncShiftLineMode = (held: boolean, point: StrokePoint | null): void => {
      if (!stateRef.current.drawing) return;

      const currentAnchorIndex = stateRef.current.shiftAnchorIndex;
      if (held) {
        if (currentAnchorIndex === null) {
          const anchorIndex = stateRef.current.points.length - 1;
          if (anchorIndex < 0) return;
          stateRef.current.shiftAnchorIndex = anchorIndex;
          // 追記専用の中継へ暫定終端を送らないよう、モード突入前の点だけを確定送信する。
          flushStrokeProgress(false);
        }
        if (point !== null) stateRef.current.shiftPreviewPoint = point;
        scheduleShiftPreviewRedraw();
        return;
      }

      if (currentAnchorIndex === null) return;
      const endPoint = point ?? stateRef.current.shiftPreviewPoint;
      const anchorPoint = stateRef.current.points[currentAnchorIndex];
      // 終端の筆圧だけが異なっても同一座標なら線分長は 0 のため、確定点へ追加しない。
      if (
        endPoint !== null &&
        anchorPoint !== undefined &&
        (anchorPoint.x !== endPoint.x || anchorPoint.y !== endPoint.y)
      ) {
        stateRef.current.points = appendShiftLinePoint(
          stateRef.current.points,
          currentAnchorIndex,
          endPoint
        );
      }
      stateRef.current.shiftRedrawAnchorIndex = currentAnchorIndex;
      stateRef.current.shiftAnchorIndex = null;
      stateRef.current.shiftPreviewPoint = null;
      scheduleShiftPreviewRedraw();
      // keyup 直後にポインタが止まっていても、他参加者のゴーストを直線終端まで追いつかせる。
      flushStrokeProgress(false);
    };

    // 未確定のペン入力を破棄する共通後始末。pointercancel（ブラウザ / OS 都合の中断）と、
    // pointerup を伴わない予期しない pointer capture 喪失で使う。部分ストロークは永続化せず、
    // 点列・stroke ID・送信カウンタ・入力中プレビューをまとめて片付けて次の描画を可能にする。
    const discardDrawing = (): void => {
      cancelShiftRedraw();
      stateRef.current.drawing = false;
      stateRef.current.drawingPointerId = null;
      stateRef.current.points = [];
      stateRef.current.strokePattern = "solid";
      stateRef.current.previewDistance = 0;
      stateRef.current.lastPreviewPoint = null;
      stateRef.current.shiftAnchorIndex = null;
      stateRef.current.shiftPreviewPoint = null;
      stateRef.current.shiftRedrawAnchorIndex = null;
      stateRef.current.strokeId = null;
      stateRef.current.sentPointCount = 0;
      clearPenPreview();
    };

    const onPointerDown = (event: PointerEvent): void => {
      const view = useCanvasStore.getState().view;
      const tool = useCanvasStore.getState().tool;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore.getState().setLastPointerStagePosition(stagePoint);

      // 新しいジェスチャの開始時に、前ジェスチャで立てた contextmenu 抑止フラグをリセットする
      // （進行中のパン中は落とさない）。
      if (!stateRef.current.panning) suppressContextMenuRef.current = false;

      // 参照専用（埋め込み）: 選択ツールが無いので、左・中・右いずれのボタンのドラッグでもキャンバス移動
      // にする。pointerdown 即時にはパンせず、一定距離ドラッグされて初めてパンへ昇格させる（その場クリックは
      // リンクカード等の要素側クリックを活かすためパンにしない）。中ボタンはブラウザ既定の autoscroll を抑止する。
      if (readOnly) {
        if (event.button === MIDDLE_BUTTON) event.preventDefault();
        if (tool === "idle" && event.target instanceof Node && container.contains(event.target)) {
          // 保留を張ると同時に data-canvas-grab が付き、押下中は subtree 全体が grab になる。
          setReadOnlyPanPending({
            x: event.clientX,
            y: event.clientY,
            panX: view.panX,
            panY: view.panY,
            pointerId: event.pointerId,
            isSecondary: event.button === SECONDARY_BUTTON,
          });
        }
        return;
      }

      // 選択操作の左右反転。パン用ボタンは反転 OFF=左、反転 ON=右。参照専用では反転しない。
      const invert = useCanvasStore.getState().invertSelectPan;
      const panButton = !readOnly && invert ? SECONDARY_BUTTON : PRIMARY_BUTTON;

      // 中ボタン（ホイール押し込み）ドラッグは、どのツール・どのモードでもキャンバス移動にする
      // （FigJam / Confluence 相当）。カーソル下に図形があっても選択せずに移動できる（object 側は
      // button!==0 の pointerdown を stopPropagation せず無視するため、イベントが container まで伝播する）。
      // 既に描画 / 図形配置ドラッグ / パン中のときは割り込ませない。
      if (
        event.button === MIDDLE_BUTTON &&
        !stateRef.current.drawing &&
        !stateRef.current.shapePlacing &&
        !stateRef.current.panning
      ) {
        // ブラウザ既定の中ボタン autoscroll（丸いスクロールカーソル）を抑止する。
        event.preventDefault();
        beginPan(event, view, tool, false);
        return;
      }

      // 主ボタン（左）以外の処理。反転 ON のパン用ボタン（右）による空白部パンだけをここで扱い、
      // それ以外（図形上の右クリック＝コンテキストメニュー、反転 OFF の右ドラッグ＝マーキー）は
      // use-marquee-selection / canvas-objects-layer 側へ委ねる。
      if (event.button !== PRIMARY_BUTTON) {
        if (
          !readOnly &&
          event.button === panButton &&
          tool === "idle" &&
          event.target === container
        ) {
          // 反転 ON の右ボタンはパン専用。右クリック（ドラッグ無し）は貼り付けメニューを開くだけなので、
          // ここで選択解除はしない（右クリックで意図せず現在の選択が消えるのを防ぐ）。
          beginPan(event, view, tool, true);
        }
        return;
      }

      // ---- 以下は主ボタン（左）のみ（参照専用は上で全ボタン処理済み）----

      // 編集中の本文・入力欄（contentEditable / input / textarea / select とその子孫）の上で始まった
      // 主ボタンのポインタは、文字選択・キャレット操作として編集要素に任せる。ペン描画・配置・パンの
      // いずれもポインタを捕捉しない（Issue #160）。中ボタンのパンは上で処理済みで、従来どおり使える。
      if (isEditablePointerTarget(event)) return;

      if (tool === "draw-pen") {
        // 既に別のポインタが描画を所有しているあいだは、新しいポインタへ所有権を渡さない。
        // ここを素通しすると drawingPointerId が差し替わり、元の所有ポインタの move / up が
        // 無視されて、1 本目が未確定のまま描画状態だけが残る（多点入力での所有権喪失）。
        if (stateRef.current.drawing) return;
        const clippedStagePoint = clampPointToCanvas(stagePoint);
        stateRef.current.drawing = true;
        stateRef.current.drawingPointerId = event.pointerId;
        stateRef.current.points = [
          { x: clippedStagePoint.x, y: clippedStagePoint.y, pressure: pressureOf(event) },
        ];
        stateRef.current.strokePattern = resolveStrokePattern(
          useCanvasStore.getState().penStrokePattern
        );
        stateRef.current.previewDistance = 0;
        stateRef.current.lastPreviewPoint = stateRef.current.points[0] ?? null;
        cancelShiftRedraw();
        stateRef.current.shiftAnchorIndex = null;
        stateRef.current.shiftPreviewPoint = null;
        stateRef.current.shiftRedrawAnchorIndex = null;
        stateRef.current.strokeId = generateStrokeId();
        stateRef.current.sentPointCount = 0;
        stateRef.current.lastTransientAt = 0;
        if (stateRef.current.strokePattern === "dotted") {
          redrawPenPreview(stateRef.current.points, null);
        }
        container.setPointerCapture(event.pointerId);
        return;
      }

      if (isPlaceDragTool(tool)) {
        // 既に別ポインタが配置を所有している間は開始点・capture 所有権を奪わない。
        if (stateRef.current.shapePlacing) return;
        // 当初仕様（キャンバス外にも配置可）へ戻したため、ドラッグ開始点がキャンバス外でも拒否しない。
        // 付箋・テキスト・図形・セクション・表は「シングルクリック=記憶したサイズ（無ければ既定サイズ）」
        // 「ドラッグ=任意サイズ」を pointerup で判別する。ここではドラッグ開始点と開始ツールを記録する
        // だけにとどめ、確定は pointerup へ委ねる（押下の時点ではオブジェクトを作らない）。
        stateRef.current.shapePlacing = true;
        stateRef.current.shapePointerId = event.pointerId;
        stateRef.current.shapeStart = {
          stageX: stagePoint.x,
          stageY: stagePoint.y,
          clientX: event.clientX,
          clientY: event.clientY,
        };
        stateRef.current.shapeDragActive = false;
        stateRef.current.shapeLastFrame = null;
        stateRef.current.placeDragTool = tool;
        shapeCaptureOwnerRef.current = container;
        container.setPointerCapture(event.pointerId);
        return;
      }

      if (tool === "place-stamp" || tool === "place-comment") {
        // place-comment はオブジェクトを即作成せず、クリック位置に入力ウィンドウを開く
        // （実際の作成は入力確定時）。onPlaceObject 側でツール種別に応じて分岐する。
        handlers.onPlaceObject({ stagePoint });
        // スタンプは明示的に解除するまで連続配置し、コメントだけは従来どおり単発で終了する。
        if (tool === "place-comment") useCanvasStore.getState().setTool("idle");
        return;
      }

      if (tool === "idle" && event.target === container) {
        // 空白部の左クリックで既存の選択を解除する。ただし追加選択の修飾キー（Ctrl/Meta/Shift）を
        // 押しているときは解除しない——反転 ON では左ドラッグが範囲選択（マーキー）で、ここで解除すると
        // use-marquee-selection が pointerup で参照する既存選択が空になり、Ctrl+ドラッグの「追加選択」が
        // 「置換」に化けてしまう（追加選択の破壊を防ぐ）。反転 OFF でも修飾キー無しの単純クリックでのみ解除する。
        const additive = event.ctrlKey || event.metaKey || event.shiftKey;
        if (!additive) {
          useCanvasStore.getState().setSelectedObjectIds([]);
        }
        // 反転 OFF では左ドラッグでパン。反転 ON では左は範囲選択（マーキー）担当のためパンしない。
        if (event.button === panButton) {
          beginPan(event, view, tool, false);
        }
      }
    };

    const onPointerMove = (event: PointerEvent): void => {
      const view = useCanvasStore.getState().view;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore.getState().setLastPointerStagePosition(stagePoint);

      // 参照専用（埋め込み）: 保留中のパンがしきい値を超えてドラッグされたら、この時点でパンを開始する。
      // 開始点は pointerdown 時点の位置・view を採用し、以降の移動量をそのまま反映する（開始時の飛びを防ぐ）。
      const readOnlyPanPending = stateRef.current.readOnlyPanPending;
      if (readOnlyPanPending && !stateRef.current.panning) {
        // 保留を張ったポインタ以外の pointermove（多点タッチの別指など）ではパンを開始しない
        // （別ポインタで保留の開始点・pointerId を流用しないため。保留自体はこの指の pointerup / window の
        // pointerup で解除される）。
        if (event.pointerId !== readOnlyPanPending.pointerId) return;
        // ボタンが 1 つも押されていない pointermove では保留を破棄する（防御的。しきい値到達前は pointer
        // capture を取っておらず、保留中に container 外へ出て pointerup を取りこぼす経路の保険。通常は
        // window の pointerup / pointercancel で解除される）。参照専用は左/中/右いずれのボタンでもパンする
        // ため、主ボタン（ビット 0）だけでなく「全ボタンが離された（buttons===0）」で判定する。
        if (event.buttons === 0) {
          setReadOnlyPanPending(null);
          return;
        }
        const distance = Math.hypot(
          event.clientX - readOnlyPanPending.x,
          event.clientY - readOnlyPanPending.y
        );
        if (distance <= READONLY_PAN_ACTIVATE_PX) return;
        stateRef.current.panning = true;
        stateRef.current.panStart = {
          x: readOnlyPanPending.x,
          y: readOnlyPanPending.y,
          panX: readOnlyPanPending.panX,
          panY: readOnlyPanPending.panY,
        };
        // 右ボタン起点のパンなら、ドラッグ成立後の contextmenu を抑止する（下の panning ブロックで
        // panMoved 到達時に suppressContextMenuRef を立てる）。他ボタンでは抑止しない。
        stateRef.current.panMoved = false;
        stateRef.current.panIsSecondary = readOnlyPanPending.isSecondary;
        // 昇格と同時に data-canvas-grab を外す。ここを外さないと grab と grabbing が同時に当たる。
        setReadOnlyPanPending(null);
        useCanvasStore.getState().setTool("panning");
        container.setPointerCapture(readOnlyPanPending.pointerId);
        // この後の panning ブロックで、開始点からの移動量に応じて view を平行移動する。
      }

      // ドラッグ配置中: しきい値を超えたらプレビュー枠を更新する。

      const shapeStart = stateRef.current.shapeStart;
      const placeDragTool = stateRef.current.placeDragTool;
      if (stateRef.current.shapePlacing && shapeStart && placeDragTool) {
        // 配置開始ポインタ以外の移動で、しきい値判定やプレビューを更新しない。
        if (event.pointerId !== stateRef.current.shapePointerId) return;
        // Esc などで resetMode され、ツールが開始ツールから外れたらジェスチャを終了する。
        // これをしないと、取り消し後の pointermove でプレビュー枠が復活してしまう
        // （最終的な配置は pointerup 側で抑止済み）。
        if (useCanvasStore.getState().tool !== placeDragTool) {
          discardShapePlacement(true);
          return;
        }
        const distance = Math.hypot(
          event.clientX - shapeStart.clientX,
          event.clientY - shapeStart.clientY
        );
        if (!stateRef.current.shapeDragActive) {
          // しきい値未満の手ブレでは枠を出さない（シングルクリック配置を保護する）。
          if (distance <= SHAPE_DRAG_THRESHOLD_PX) return;
          stateRef.current.shapeDragActive = true;
        }
        const frame = computePlaceDragFrame(
          placeDragTool,
          { x: shapeStart.stageX, y: shapeStart.stageY },
          stagePoint,
          event.shiftKey
        );
        stateRef.current.shapeLastFrame = frame;
        useCanvasStore.getState().setShapePlacementDraft({
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          arrowFromCorner: frame.arrowFromCorner,
          arrowFromEdge: frame.arrowFromEdge,
        });
        return;
      }

      if (stateRef.current.drawing) {
        // 描画を開始したポインタ以外の move は無視する（多点入力で別ポインタの座標が
        // 進行中ストロークへ混入するのを防ぐ）。
        if (event.pointerId !== stateRef.current.drawingPointerId) return;
        const clippedStagePoint = clampPointToCanvas(stagePoint);
        const currentPoint = {
          x: clippedStagePoint.x,
          y: clippedStagePoint.y,
          pressure: pressureOf(event),
        };
        const shiftAnchorBeforeSync = stateRef.current.shiftAnchorIndex;
        syncShiftLineMode(event.shiftKey, currentPoint);
        if (stateRef.current.shiftAnchorIndex !== null) return;
        // keyup を取りこぼした同期経路では、現在点を直線終端として既に確定している。
        // 同じ点を通常手書きとして重ねず、次の pointermove から追記を再開する。
        if (shiftAnchorBeforeSync !== null) return;
        stateRef.current.points.push(currentPoint);
        // 入力中ストロークは Canvas2D に増分描画する（暫定：全描画 redraw でなく
        // 直前ポイントから現在ポイントまでをライン描画しても十分滑らか）
        const handle = options.penLayerRef.current;
        if (handle) {
          const ctx = handle.canvas.getContext("2d");
          if (ctx) {
            const color = useCanvasStore.getState().penColor;
            const thickness = PEN_THICKNESS_PIXELS[useCanvasStore.getState().penThickness];
            ctx.save();
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.strokeStyle = color;
            ctx.lineWidth = thickness;
            const last = stateRef.current.lastPreviewPoint;
            const cur = currentPoint;
            if (last && cur) {
              const distanceBefore = stateRef.current.previewDistance;
              applyCanvasStrokePattern(
                ctx,
                thickness,
                stateRef.current.strokePattern,
                distanceBefore
              );
              ctx.beginPath();
              // ステージ座標からプレビュー矩形（固定ステージ ∪ キャンバス矩形）の原点を差し引いて
              // プレビュー Canvas ローカル座標へ変換する。
              ctx.moveTo(last.x - penPreviewRect.x, last.y - penPreviewRect.y);
              ctx.lineTo(cur.x - penPreviewRect.x, cur.y - penPreviewRect.y);
              ctx.stroke();
              stateRef.current.previewDistance =
                distanceBefore + Math.hypot(cur.x - last.x, cur.y - last.y);
              stateRef.current.lastPreviewPoint = cur;
            }
            ctx.restore();
          }
        }
        // 他参加者へ描画中ストロークの増分点を中継する（一定間隔で間引く）。
        flushStrokeProgress(true);
        return;
      }

      if (stateRef.current.panning && stateRef.current.panStart) {
        const start = stateRef.current.panStart;
        // しきい値を超えて動いたら「実際にドラッグした」とみなす。右ボタン起点のパンが動いたときは、
        // 直後の contextmenu を抑止対象にする（パン操作がメニュー表示に化けないようにする）。
        if (!stateRef.current.panMoved) {
          const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
          if (distance > PAN_MOVE_THRESHOLD_PX) {
            stateRef.current.panMoved = true;
            if (stateRef.current.panIsSecondary) {
              suppressContextMenuRef.current = true;
            }
          }
        }
        useCanvasStore.getState().setView({
          ...view,
          panX: start.panX + (event.clientX - start.x),
          panY: start.panY + (event.clientY - start.y),
        });
      }
    };

    const onPointerUp = (event: PointerEvent): void => {
      // ドラッグ配置（付箋 / テキスト / 図形 / セクション / 表）のジェスチャの確定。
      // ドラッグ成立なら任意サイズ、未成立ならシングルクリック扱い。
      if (stateRef.current.shapePlacing) {
        // 所有ポインタ以外の pointerup / pointercancel は配置の確定・破棄に使わない。
        if (event.pointerId !== stateRef.current.shapePointerId) return;
        // callback より先に全状態を初期化する。callback が同期的に再レンダーや次入力を起こしても、
        // 完了済みジェスチャを新しいハンドラが再処理しない。
        const placement = takeShapePlacement();
        const { shapeStart, dragActive, lastFrame, placeDragTool } = placement;
        // 配置せず状態だけ破棄するケース:
        // - pointercancel（ブラウザによる中断）
        // - ドラッグ中に Esc などでツールが開始ツールから外れた（resetMode による取り消し）
        const cancelled =
          event.type === "pointercancel" ||
          placeDragTool === null ||
          useCanvasStore.getState().tool !== placeDragTool;
        if (shapeStart && placeDragTool && !cancelled) {
          if (dragActive) {
            // プレビューと確定値を一致させるため、最後に表示した枠をそのまま採用する。
            // move を受け取れなかった防御的なケースだけ、pointerup 座標から再計算する。
            let frame = lastFrame;
            if (frame === null) {
              const view = useCanvasStore.getState().view;
              const end = screenToStage({ x: event.clientX, y: event.clientY }, view);
              frame = computePlaceDragFrame(
                placeDragTool,
                { x: shapeStart.stageX, y: shapeStart.stageY },
                end,
                event.shiftKey
              );
            }
            if (placeDragTool === "place-sticky") {
              handlers.onPlaceStickyDrag({ frame });
            } else if (placeDragTool === "place-text") {
              handlers.onPlaceTextDrag({ frame });
            } else if (placeDragTool === "place-section") {
              handlers.onPlaceSectionDrag({ frame });
            } else if (placeDragTool === "place-table") {
              handlers.onPlaceTableDrag({ frame });
            } else {
              handlers.onPlaceShapeDrag({ frame });
            }
          } else {
            // しきい値未満＝シングルクリック。記憶したサイズ（無ければ既定サイズ）で配置する。
            handlers.onPlaceObject({
              stagePoint: { x: shapeStart.stageX, y: shapeStart.stageY },
            });
          }
        }
        // ツールが開始ツールのままのとき（=外部から変更されていないとき）だけ idle へ戻す。
        // Esc やツール切替で既に別ツールへ変わっている場合は、その選択を尊重して上書きしない。
        if (placeDragTool && useCanvasStore.getState().tool === placeDragTool) {
          useCanvasStore.getState().setTool("idle");
        }
        releaseShapePointerCapture(placement.captureOwner, placement.pointerId);
        return;
      }
      // 所有ポインタ以外の pointerup / pointercancel では、進行中の描画を確定も破棄もしない。
      if (stateRef.current.drawing && event.pointerId === stateRef.current.drawingPointerId) {
        if (event.type === "pointercancel") {
          // ブラウザ / OS 都合の中断。部分ストロークを確定させず、未確定入力を破棄する。
          discardDrawing();
        } else {
          const anchorIndex = stateRef.current.shiftAnchorIndex;
          if (anchorIndex !== null) {
            const view = useCanvasStore.getState().view;
            const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
            const clippedStagePoint = clampPointToCanvas(stagePoint);
            const endPoint = {
              x: clippedStagePoint.x,
              y: clippedStagePoint.y,
              pressure: pressureOf(event),
            };
            const anchor = stateRef.current.points[anchorIndex];
            stateRef.current.points =
              anchor && (anchor.x !== endPoint.x || anchor.y !== endPoint.y)
                ? appendShiftLinePoint(stateRef.current.points, anchorIndex, endPoint)
                : stateRef.current.points.slice(0, anchorIndex + 1);
          }
          cancelShiftRedraw();
          stateRef.current.shiftAnchorIndex = null;
          stateRef.current.shiftPreviewPoint = null;
          stateRef.current.shiftRedrawAnchorIndex = null;
          const points = stateRef.current.points;
          const strokeId = stateRef.current.strokeId;
          const strokePattern = stateRef.current.strokePattern;
          stateRef.current.drawing = false;
          stateRef.current.drawingPointerId = null;
          // 従来の実線は 2 点以上でだけ確定する。点線は短いストロークを始点の丸で
          // 可視化できるため、1 点入力も永続化する。
          if (
            (points.length >= 2 || (points.length === 1 && strokePattern === "dotted")) &&
            strokeId
          ) {
            const color = useCanvasStore.getState().penColor;
            const thickness = PEN_THICKNESS_PIXELS[useCanvasStore.getState().penThickness];
            handlers.onStrokeCommit({ strokeId, points, color, thickness, strokePattern });
          }
          // 1 点だけで確定しない場合も、直線モードの暫定描画を残さない。
          clearPenPreview();
          stateRef.current.points = [];
          stateRef.current.strokePattern = "solid";
          stateRef.current.previewDistance = 0;
          stateRef.current.lastPreviewPoint = null;
          // ストローク確定後は中継状態をリセットする（確定 op 側でゴーストが破棄される）。
          stateRef.current.strokeId = null;
          stateRef.current.sentPointCount = 0;
        }
      }
      // 参照専用のパン保留（しきい値未満で離した＝その場クリック）は破棄し、grab も外す。
      setReadOnlyPanPending(null);
      if (stateRef.current.panning) {
        stateRef.current.panning = false;
        stateRef.current.panStart = null;
        // パン開始時のツールへ復元する（中ボタンパンで配置ツール中にパンした場合など）。
        // 空白部の通常パンは開始時 idle なので、従来どおり idle へ戻る。
        const restore = stateRef.current.toolBeforePan ?? "idle";
        stateRef.current.toolBeforePan = null;
        stampPanCaptureRef.current = null;
        stateRef.current.panMoved = false;
        stateRef.current.panIsSecondary = false;
        const currentState = useCanvasStore.getState();
        // パン中の Esc や別ツール選択による明示的な解除を、旧スタンプの復元で取り消さない。
        if (restore !== "place-stamp" || currentState.tool === "panning") {
          currentState.setTool(restore);
        }
        // pointercancel（ブラウザ/OS 中断・タッチキャンセル）で終わった右ボタンパンの後は contextmenu が
        // 発火しないため、立てた抑止フラグを残さない（次の正当な contextmenu を誤抑止しないため）。
        if (event.type === "pointercancel") {
          suppressContextMenuRef.current = false;
        }
      }
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
    };

    // 参照専用パンの保留（readOnlyPanPending）は、しきい値到達まで pointer capture を取らないため、
    // 保留中に container 外へポインタが出て pointerup を container で取りこぼすと保留が残り得る。
    // ポインタが「どこで離されても」保留を確実に解除するため、window でも pointerup / pointercancel を
    // 監視して保留を破棄する（capture を取らない＝リンクカード等のクリックは阻害しない、という前提を維持
    // したまま、取りこぼしによる保留リークを塞ぐ）。pan 開始後は capture 済みで container 側が受け取るため、
    // ここでは保留の解除だけを担い、panning 状態は container の onPointerUp が確定的に片付ける。
    // window の blur も同じ解除経路へ入れる。保留中（capture 未取得）に Alt+Tab などでウィンドウが
    // フォーカスを失い、そのまま別ウィンドウ上でボタンを離すと pointerup / pointercancel はこの文書へ
    // 届かず、data-canvas-grab が次の pointermove まで残って grab カーソルが居座るため。
    // 昇格後は保留が null なので、パン中に blur しても（capture 済みで pointerup は届く）ここは無害である。
    const clearReadOnlyPanPending = (): void => {
      setReadOnlyPanPending(null);
    };

    /** Shift キーそのものの押下・解放を、描画中の直線モード境界として扱う。 */
    const onShiftKeyChange = (event: KeyboardEvent): void => {
      if (event.key !== "Shift") return;
      syncShiftLineMode(event.shiftKey, null);
    };

    // pointerup を伴わない pointer capture 喪失（他ハンドラによる解放・要素差し替え・OS 都合の
    // 中断など）の受け皿。描画状態が残ったままだと、ボタンを離した後のポインタ移動にまで線が
    // 追従し続けるため、未確定入力を破棄して次の描画へ復帰できるようにする。
    // 通常の pointerup は確定と capture 解放を済ませてから lostpointercapture が届くので、
    // その時点では配置 / drawing が false であり、ここで二重処理はしない。
    const onLostPointerCapture = (event: PointerEvent): void => {
      // 子要素からバブルした喪失や別ポインタの喪失は、コンテナ自身が所有する配置を中断しない。
      if (
        stateRef.current.shapePlacing &&
        event.target === container &&
        event.pointerId === stateRef.current.shapePointerId
      ) {
        // capture は既に失われているため再解放せず、配置状態とプレビューだけを破棄する。
        discardShapePlacement(false);
        return;
      }
      // パン中にキャプチャを失った場合は、パン状態を残さず onPointerUp と同じ後始末を行う。
      // 残すと onPointerMove のパン分岐（ボタン状態を見ない）が古い panStart を基準に働き続け、
      // ボタンを押していない移動で表示位置が動いてしまう。
      // 判定を event.target === container に限定するのは、オブジェクトのドラッグ（use-object-drag.ts）が
      // 子要素へ設定したキャプチャの解放もバブルして届くためで、パンのキャプチャは beginPan が
      // コンテナ自身へ設定する。通常のパン終了でもこのイベントは届くが、そのときは onPointerUp が
      // 先に走って panning が false になっているため、ここは二重処理にならない。
      // 右ボタン起点のパンで立てた contextmenu 抑止フラグは、ここでは解除しない。キャプチャを
      // 失っても物理ボタンは押されたままで、離した時点で contextmenu が発火し得るため
      // （イベントが来ないことが確定している pointercancel とは異なる）、抑止を維持しないと
      // パン直後にメニューが開いてしまう。
      if (stateRef.current.panning && event.target === container) {
        stateRef.current.panning = false;
        stateRef.current.panStart = null;
        const restore = stateRef.current.toolBeforePan ?? "idle";
        stateRef.current.toolBeforePan = null;
        stampPanCaptureRef.current = null;
        stateRef.current.panMoved = false;
        stateRef.current.panIsSecondary = false;
        const currentState = useCanvasStore.getState();
        // パン中の Esc や別ツール選択による明示的な解除を、旧スタンプの復元で取り消さない。
        if (restore !== "place-stamp" || currentState.tool === "panning") {
          currentState.setTool(restore);
        }
        return;
      }
      if (!stateRef.current.drawing) return;
      if (event.pointerId !== stateRef.current.drawingPointerId) return;
      discardDrawing();
    };

    // 右ボタンドラッグによるパン（反転 ON）直後の contextmenu を抑止する。
    // window の capture フェーズで、ブラウザ標準メニュー・空白部の貼り付けメニューより先に止める。
    // 抑止対象はパンが実際に動いたときだけ（右シングルクリックのメニューは従来どおり出す）。
    const onContextMenu = (event: MouseEvent): void => {
      if (!suppressContextMenuRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressContextMenuRef.current = false;
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerUp);
    container.addEventListener("lostpointercapture", onLostPointerCapture);
    window.addEventListener("pointerup", clearReadOnlyPanPending);
    window.addEventListener("pointercancel", clearReadOnlyPanPending);
    window.addEventListener("blur", clearReadOnlyPanPending);
    window.addEventListener("keydown", onShiftKeyChange);
    window.addEventListener("keyup", onShiftKeyChange);
    window.addEventListener("contextmenu", onContextMenu, { capture: true });
    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerUp);
      container.removeEventListener("lostpointercapture", onLostPointerCapture);
      window.removeEventListener("pointerup", clearReadOnlyPanPending);
      window.removeEventListener("pointercancel", clearReadOnlyPanPending);
      window.removeEventListener("blur", clearReadOnlyPanPending);
      window.removeEventListener("keydown", onShiftKeyChange);
      window.removeEventListener("keyup", onShiftKeyChange);
      window.removeEventListener("contextmenu", onContextMenu, { capture: true });
      // 保留を表す属性が旧コンテナへ残らないようにする（アンマウント・コンテナ差し替え対策）。
      // 保留自体が続いている場合は、次のレンダーで貼り直す effect 冒頭の同期が復元する。
      container.removeAttribute(READONLY_GRAB_ATTRIBUTE);
      cancelShiftRedraw();
    };
  });
}

function pressureOf(event: PointerEvent): number {
  // ペンデバイスのみ筆圧を採用、それ以外は均一な太さで描画
  if (event.pointerType === "pen" && event.pressure > 0) {
    return event.pressure;
  }
  return 0.5;
}
